import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { EtlapSor, etlapNaponta, helyiNap, hetHetfoje, napHozzaad } from './etlap-kozos';

interface EtlapNap { datum: string; cim: string; ma: boolean; sorok: EtlapSor[]; }

/**
 *  HETI ÉTLAP – mit főz a konyha a héten, menünként (GET /etkezes-etlap/lista).
 *
 *  Két helyen él:
 *    • az Étkezés képernyőn panelként, a rendelés fölött – ott a szülő ebből látja, mi
 *      van az „A” és a „B” mögött. Ha az intézmény nem tölt fel étlapot, a panel meg
 *      sem jelenik (`rejtUresen`): egy állandóan üres doboz csak zaj;
 *    • az Étlap oldalon (/etlap) – a tanulónak, aki nem rendel, de tudni szeretné,
 *      mi lesz az ebéd.
 *  Hétről hétre lapozható; a hétvége csak akkor látszik, ha arra is van étlap.
 */
@Component({
  selector: 'app-etlap-heti',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './etlap-heti.component.html',
  styleUrls: ['./etlap.component.scss'],
})
export class EtlapHetiComponent implements OnInit, OnChanges {
  /** Az intézmény azonosítója (sajatIntezmeny.intezmenyId). */
  @Input() intezmeny = '';
  /** Kártya címe. */
  @Input() cim = 'Heti étlap';
  /** Ha a mostani és a jövő héten sincs étlap, a kártya nem jelenik meg. */
  @Input() rejtUresen = false;

  eltolas = 0;               // hány héttel a mostani után (negatív: korábbi hét)
  napok: EtlapNap[] = [];
  fut = false;
  hiba = '';
  /** `rejtUresen` mellett: volt-e egyáltalán mit mutatni az első betöltéskor. */
  lathato = true;
  private elsoBetoltes = true;

  constructor(private http: HttpClient) {}

  ngOnInit() { this.betolt(); }

  ngOnChanges(v: SimpleChanges) {
    if (v['intezmeny'] && !v['intezmeny'].firstChange) { this.elsoBetoltes = true; this.betolt(); }
  }

  get hetfo(): Date { return napHozzaad(hetHetfoje(new Date()), this.eltolas * 7); }

  get hetCim(): string {
    const f = (d: Date) => `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
    const elotag = this.eltolas === 0 ? 'Ez a hét' : this.eltolas === 1 ? 'Jövő hét' : this.eltolas === -1 ? 'Múlt hét' : '';
    return `${elotag ? elotag + ': ' : ''}${f(this.hetfo)} – ${f(napHozzaad(this.hetfo, 4)).slice(6)}`;
  }

  get vanAdat(): boolean { return this.napok.some((n) => n.sorok.length); }

  lapoz(lepes: number) {
    this.eltolas = lepes === 0 ? 0 : this.eltolas + lepes;
    this.betolt();
  }

  betolt() {
    if (!this.intezmeny) return;
    const tol = this.hetfo;
    //  Az első betöltésnél két hetet kérünk: így derül ki, van-e értelme a panelnek
    //  (péntek délután a jövő heti étlap a lényeg, a mostani már lefutott).
    const napokSzama = this.elsoBetoltes ? 14 : 7;
    const ig = napHozzaad(tol, napokSzama - 1);
    this.fut = true;
    this.hiba = '';
    this.http.get<any>(`/api/etkezes-etlap/lista/${this.intezmeny}`,
      { params: { tol: helyiNap(tol), ig: helyiNap(ig) } }).subscribe({
      next: (r) => {
        const sorok: EtlapSor[] = r?.sorok || [];
        if (this.elsoBetoltes) {
          this.lathato = !this.rejtUresen || sorok.length > 0;
          this.elsoBetoltes = false;
        }
        this.napok = this.hetNapjai(tol, sorok);
        this.fut = false;
      },
      error: (e) => {
        this.fut = false;
        this.napok = this.hetNapjai(tol, []);
        this.hiba = e?.error?.error || 'Az étlap nem tölthető be.';
        if (this.elsoBetoltes && this.rejtUresen) this.lathato = false;
        this.elsoBetoltes = false;
      },
    });
  }

  private hetNapjai(hetfo: Date, sorok: EtlapSor[]): EtlapNap[] {
    const naponta = etlapNaponta(sorok);
    const ma = helyiNap(new Date());
    const napNevek = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];
    const lista: EtlapNap[] = [];
    for (let i = 0; i < 7; i++) {
      const d = napHozzaad(hetfo, i);
      const datum = helyiNap(d);
      const napSorai = naponta[datum] || [];
      if (i >= 5 && !napSorai.length) continue;             // üres hétvége: nem mutatjuk
      lista.push({
        datum, ma: datum === ma, sorok: napSorai,
        cim: `${napNevek[i]}, ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`,
      });
    }
    return lista;
  }

  /** Több beszállítónál a menü-jel elé kerül a beszállító neve, különben két „A” lenne. */
  get tobbBeszallito(): boolean {
    const h = new Set(this.napok.flatMap((n) => n.sorok.map((s) => s.hely_nev || '')));
    return h.size > 1;
  }

  trackNap = (_: number, n: EtlapNap) => n.datum;
}
